/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-06-12 01:01:12
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, getFormValues } from 'redux-form';
import { FormLayout, Link } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderAutocomplete } from 'src/components/views/common/autocomplete-hoc';
import {
  FILTER_OPTION_DISPLAY_TYPES_OPTION_TYPE,
  FILTER_OPTION_SELECT_TYPES,
  FILTER_OPTION_SWATCH_STYLES,
  getFilterOptionValueTypes
} from 'src/constants/filter-tree';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { routes } from 'src/routes';
import { getShop } from 'src/helpers';
import { URL } from 'src/constants/links';

type Props = {
  type: string,
  optionType: String,
  optionsList: Array<Object>,
  valueType: string,
  displayType: string,
  manualValues: Array<Object>,
  excludedValues: Array<Object>,
  themeSetupV2: boolean,
  host: string,
  appLanguage: string
};

type ComposedProps = Props & WithI18nProps;

class MetafieldType extends Component<ComposedProps> {
  render() {
    const { i18n } = this.props;
    const displayTypes = FILTER_OPTION_DISPLAY_TYPES_OPTION_TYPE.map(function (type) {
      return {
        ...type,
        label: i18n.translate(type.label)
      };
    });
    const selectTypes = FILTER_OPTION_SELECT_TYPES.map(function (type) {
      return {
        ...type,
        label: i18n.translate(type.label)
      };
    });

    const swatchStyles = FILTER_OPTION_SWATCH_STYLES.map(function (style) {
      return {
        ...style,
        label: i18n.translate(style.label)
      };
    });

    const {
      optionType,
      valueType,
      optionsList,
      displayType,
      manualValues,
      excludedValues,
      themeSetupV2,
      host,
      appLanguage
    } = this.props;
    const valueTypes = getFilterOptionValueTypes(optionType, appLanguage, i18n);
    const shop = getShop();

    return (
      <FormLayout>
        <Field
          name="metafield_DisplayType"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionDisplay_Label'
          )}
          options={displayTypes}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionDisplay_Tooltip'
          )}
          helperText={
            displayType === 'swatch' &&
            (themeSetupV2 ? (
              <span>
                <span style={{ color: '#DE3618', fontWeight: 500 }}>
                  {i18n.translate(
                    'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText_ImportantNOTE'
                  )}
                </span>{' '}
                {i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText1_Content',
                  {
                    t: (
                      <Link
                        external
                        url={`${routes.swatchSettings.view()}?shop=${shop}&host=${host}`}>
                        {i18n.translate(
                          'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText1_Content_t'
                        )}
                      </Link>
                    )
                  }
                )}
              </span>
            ) : (
              <span>
                <span style={{ color: '#DE3618', fontWeight: 500 }}>
                  {i18n.translate(
                    'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText_ImportantNOTE'
                  )}
                </span>{' '}
                {i18n.translate(
                  'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText2_Content',
                  {
                    t: (
                      <Link external url={URL.OptionTypeSwatchDoc}>
                        {i18n.translate(
                          'AdminHTML.FilterTree.Item.optionModal_GeneralTab_HelperText2_Content_t'
                        )}
                      </Link>
                    )
                  }
                )}
              </span>
            ))
          }
        />

        {displayType === 'swatch' && themeSetupV2 && (
          <Field
            name="metafield_SwatchStyle"
            component={RenderSelect}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_SwatchStyle_Label'
            )}
            options={swatchStyles}
          />
        )}

        <Field
          name="metafield_SelectType"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionSelect_Label'
          )}
          options={selectTypes}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionSelect_Tooltip'
          )}
        />

        <Field
          name="metafield_ValueType"
          component={RenderSelect}
          label={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionValues_Label'
          )}
          options={valueTypes}
          tooltip={i18n.translate(
            'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_OptionValues_Tooltip'
          )}
        />

        {valueType === 'specific' ? (
          <Field
            name="metafield_ManualValues"
            component={RenderAutocomplete}
            value={manualValues}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_ManualValues_Label'
            )}
            options={optionsList}
            placeholder={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_MetafieldType_Placeholder'
            )}
          />
        ) : (
          <Field
            name="metafield_ExcludedValues"
            component={RenderAutocomplete}
            value={excludedValues}
            label={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Field_ExcludedValues_Label'
            )}
            options={optionsList}
            tooltip={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_MetafieldType_Field5_Tooltip'
            )}
            placeholder={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_MetafieldType_Placeholder'
            )}
          />
        )}
      </FormLayout>
    );
  }
}

const mapStateToProps = (state, props) => {
  const formFilterOptionValues = getFormValues('OptionModalForm')(state);
  const optionType = _.get(formFilterOptionValues, 'optionType', '');

  const type = _.startsWith(optionType, 'metafield_')
    ? optionType.replace('metafield_', 'metafield')
    : optionType;
  const optionsList = _.get(state.filterData.filterData, type, []);

  const tenantApp = _.get(state.oauth, 'tenantApp', {});

  return {
    type,
    optionType,
    optionsList,
    valueType: _.get(formFilterOptionValues, 'metafield_ValueType', ''),
    displayType: _.get(formFilterOptionValues, 'metafield_DisplayType', ''),
    manualValues: _.get(formFilterOptionValues, 'metafield_ManualValues', []),
    excludedValues: _.get(formFilterOptionValues, 'metafield_ExcludedValues', []),
    themeSetupV2: _.get(tenantApp, 'adminAppFeatures.themeSetupV2', false),
    host: _.get(state.oauth, 'host', ''),
    appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE)
  };
};

export default connect(mapStateToProps)(withI18n()(MetafieldType));
